// @flow
import React, {forwardRef, FunctionComponent, useCallback, useReducer, useState} from 'react';
import FlexStack from "../components/Common/flex-stack";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography
} from "@mui/material";
import {useFieldArray, useForm, UseFormRegister} from "react-hook-form";
import {FarmData} from "../api/dtos/breeder-update";
import {useMutation} from "@tanstack/react-query";
import AuthService from "../services/auth-service";
import {AddressFields, OwnerFields, requiredFieldConfig} from "../components/Breeder/farm-form-sections";
import LockIcon from '@mui/icons-material/Lock';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {Trans, useTranslation} from "react-i18next";
import {PaymentService} from "../services/payment-service";
import {formatProductPrice, formatTaxHint} from "../services/product-format";
import {PlanSelection} from "../components/Subscription/plan-selection";
import {SupportNote} from "../components/Common/support-note";
import {Product} from "../services/product-service";
import {SignupProductType, useSignupProducts} from "../hooks/use-signup-products";
import {getHttpStatus} from "../util/http-error";

type SignupFailure = 'conflict' | 'generic' | 'noProduct';

type SignupData = FarmData & {
    password: string,
    plan: SignupProductType,
    passwordConfirmation: string,
};

type CheckoutTarget = {
    breederId: string,
    productId: string,
};

type SignupConfirmation = {
    terms: boolean;
    privacy: boolean;
};

const useToggle = (initialValue: boolean) => useReducer(x => !x, initialValue);

const PasswordField = forwardRef<HTMLInputElement, React.ComponentProps<typeof TextField>>((props, ref) => {
    const [showPassword, toggleShowPassword] = useToggle(false);

    return (
        <TextField
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label={showPassword ? 'hide the password' : 'display the password'}
                                onClick={toggleShowPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                        </InputAdornment>
                    )
                },
            }}
            {...props}
        />
    );
});


type PlanStepProps = {
    watch: any,
    setValue: any,
    products: Product[],
    isPending: boolean,
    isError: boolean,
};

const PlanStep: FunctionComponent<PlanStepProps> = ({watch, setValue, products, isPending, isError}) => {
    const {t} = useTranslation();
    const selectedPlan = watch('plan');

    return (
        <Box>
            <Typography variant="h6" gutterBottom>{t('signupBreeder.plan.title')}</Typography>
            <Typography variant="subtitle1" color="textSecondary" sx={{mb: 3}}>
                {t('signupBreeder.plan.subtitle')}
            </Typography>
            {isPending && <Stack alignItems="center" sx={{mb: 3}}><CircularProgress/></Stack>}
            {!isPending && isError && <Alert severity="error" sx={{mb: 3}}>{t('signupBreeder.plan.loadError')}</Alert>}
            <PlanSelection products={products} selected={selectedPlan}
                           onSelect={productType => setValue('plan', productType)}/>
        </Box>
    );
};

const AccountStep: FunctionComponent<{ watch: any, register: any, errors: any }> = ({watch, register, errors}) => {
    const password = watch('password');

    const registerRequired: UseFormRegister<SignupData & SignupConfirmation> = useCallback((fieldName, options?) => ({
        ...register(fieldName, {...requiredFieldConfig, ...options}),
        required: true,
    }), [register]);

    const {t} = useTranslation();
    return (
        <Box>
            <Typography variant="h6" gutterBottom>{t('signupBreeder.account.title')}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                {t('signupBreeder.account.subtitle')}
            </Typography>
            <FlexStack>
                <TextField
                    {...registerRequired('emailAddress')}
                    label={t('signupBreeder.account.emailLabel')}
                    type="email"
                    required
                    placeholder={t('signupBreeder.account.emailPlaceholder')}
                    error={!!errors.emailAddress}
                    helperText={errors.emailAddress?.message ?? ''}
                />
                <TextField
                    {...registerRequired('username')}
                    label={t('signupBreeder.account.usernameLabel')}
                    required
                    placeholder={t('signupBreeder.account.usernamePlaceholder')}
                    error={!!errors.username}
                    helperText={errors.username?.message ?? ''}
                />
                <PasswordField
                    {...registerRequired('password')}
                    label={t('signupBreeder.account.passwordLabel')}
                    placeholder={t('signupBreeder.account.passwordPlaceholder')}
                    error={!!errors.password}
                    helperText={errors.password?.message ?? ''}
                />
                <PasswordField
                    {...registerRequired('passwordConfirmation', {
                        validate: value => {
                            if (password !== value) {
                                return 'Passwörter stimmen nicht überein!'
                            }
                            return true;
                        }
                    })}
                    label="Passwort wiederholen"
                    error={!!errors.passwordConfirmation}
                    helperText={errors.passwordConfirmation?.message ?? ''}/>
            </FlexStack>
        </Box>
    );
};

const FarmStep: FunctionComponent<{
    register: any,
    errors: any,
    control: any
}> = ({register, errors, control}) => {
    const {t} = useTranslation();
    const {fields: owners, append: appendOwner, remove: removeOwner} = useFieldArray({control, name: "owners"});
    const addOwner = useCallback(() => appendOwner({name: ""}), [appendOwner]);
    return (
        <Box>
            <Typography variant="h6" gutterBottom>{t('signupBreeder.farm.title')}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                {t('signupBreeder.farm.subtitle')}
            </Typography>
            <FlexStack>

                <TextField
                    {...register('farmName', requiredFieldConfig)}
                    label="Hofname"
                    required
                    error={!!errors.farmName}
                    helperText={errors.farmName?.message as string ?? ''}/>
                <TextField
                    {...register('herdCode')}
                    label="Herdkürzel"
                    error={!!errors.herdCode}
                    helperText={errors.herdCode?.message as string ?? ''}/>
                <TextField label="Webseite" {...register("website")}/>
            </FlexStack>
            <Box sx={{mt: 4}}>
                <Typography variant="subtitle2" color="textSecondary"
                            sx={{
                                textTransform: 'uppercase',
                                mb: 2
                            }}>{t('signupBreeder.farm.ownersSection')}</Typography>
                <OwnerFields register={register} errors={errors} fields={owners} addOwner={addOwner}
                             removeOwner={removeOwner}/>
            </Box>
        </Box>
    );
};

const AddressStep: FunctionComponent<{ register: any, errors: any }> = ({register, errors}) => {
    const {t} = useTranslation();
    return (
        <Box>
            <Typography variant="h6" gutterBottom>{t('signupBreeder.address.title')}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                {t('signupBreeder.address.subtitle')}
            </Typography>
            <AddressFields register={register} errors={errors}/>
        </Box>
    );
};

const ConfirmationStep: FunctionComponent<{
    watch: any,
    register: any,
    errors: any,
    selectedProduct?: Product
}> = ({watch, register, errors, selectedProduct}) => {
    const {t} = useTranslation();
    const data = watch();
    const planLabel = selectedProduct
        ? t('signupBreeder.confirmation.planSummary', {
            name: t(selectedProduct.name),
            price: formatProductPrice(t, selectedProduct),
            tax: formatTaxHint(t, selectedProduct),
        })
        : '–';

    return (
        <Box>
            <Typography variant="h6" gutterBottom>{t('signupBreeder.confirmation.title')}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                {t('signupBreeder.confirmation.subtitle')}
            </Typography>

            <Paper variant="outlined" sx={{p: 2, mb: 2, bgcolor: 'action.hover', borderColor: 'primary.light'}}>
                <Typography variant="caption" color="primary" fontWeight="bold"
                            sx={{textTransform: 'uppercase', display: 'block', mb: 0.5}}>
                    {t('signupBreeder.confirmation.selectedPlan')}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="primary.dark">{planLabel}</Typography>
                <Typography variant="caption" color="primary">{t('signupBreeder.confirmation.trialNote')}</Typography>
            </Paper>

            <Paper variant="outlined" sx={{p: 2, mb: 2}}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold"
                            sx={{textTransform: 'uppercase', display: 'block', mb: 1}}>
                    {t('signupBreeder.confirmation.farmSection')}
                </Typography>
                <Stack spacing={1}>
                    <SummaryRow label={t('signupBreeder.confirmation.summaryFarmName')} value={data.farmName}/>
                    <SummaryRow label={t('signupBreeder.confirmation.summaryOwner')}
                                value={data.owners?.map((o: any) => o.name).join(', ')}/>
                    <SummaryRow label={t('signupBreeder.confirmation.summaryAddress')}
                                value={[data.address?.street, data.address?.zipCode, data.address?.city, data.address?.country].filter(Boolean).join(", ")}/>
                    <SummaryRow label={t('signupBreeder.confirmation.summaryEmail')} value={data.emailAddress}/>
                </Stack>
            </Paper>

            <Stack gap={1} sx={{mb: 3}}>
                <FormControl required error={errors.terms}>
                    <FormControlLabel control={<Checkbox {...register('terms', requiredFieldConfig)} />}
                                      label={<Typography variant="body2">
                                          <Trans i18nKey="signupBreeder.confirmation.termsLabel">
                                              Ich habe die <a
                                              href={t('footer.terms.url')}
                                              target="_blank" rel="noreferrer">AGB</a> gelesen und akzeptiere sie.
                                          </Trans>
                                      </Typography>}/>
                    {errors.terms && <FormHelperText>{errors.terms.message}</FormHelperText>}
                </FormControl>
                <FormControl required error={errors.privacy}>
                    <FormControlLabel control={<Checkbox {...register('privacy', requiredFieldConfig)} />}
                                      label={<Typography variant="body2">
                                          <Trans i18nKey="signupBreeder.confirmation.privacyLabel">
                                              Ich habe die <a
                                              href={t('footer.privacy.url')}
                                              target="_blank" rel="noreferrer">Datenschutzerklärung</a> zur Kenntnis
                                              genommen.
                                          </Trans>
                                      </Typography>}/>
                    {errors.privacy && <FormHelperText>{errors.privacy.message}</FormHelperText>}
                </FormControl>
            </Stack>

            <Paper variant="outlined"
                   sx={{p: 2, bgcolor: 'grey.50', display: 'flex', gap: 2, alignItems: 'flex-start'}}>
                <LockIcon color="action" fontSize="small" sx={{mt: 0.2}}/>
                <Typography variant="body2" color="textSecondary">
                    <Trans i18nKey="signupBreeder.confirmation.paymentNote">
                        Nach dem Klick auf <strong>„Weiter zur Zahlung"</strong> wirst du zu unserem sicheren
                        Zahlungsanbieter Creem weitergeleitet.
                    </Trans>
                </Typography>
            </Paper>
        </Box>
    );
};

const SummaryRow = ({label, value}: { label: string, value: string }) => (
    <Stack direction="row" justifyContent="space-between"
           sx={{py: 1, borderBottom: '1px solid', borderColor: 'grey.100', '&:last-child': {borderBottom: 0}}}>
        <Typography variant="body2" color="textSecondary">{label}</Typography>
        <Typography variant="body2" fontWeight="medium">{value || "–"}</Typography>
    </Stack>
);

const SignupBreeder: FunctionComponent = () => {
    const {t} = useTranslation();
    const steps = [
        t('signupBreeder.stepAccount'),
        t('signupBreeder.stepFarmAndOwner'),
        t('signupBreeder.stepAddress'),
        t('signupBreeder.stepPlan'),
        t('signupBreeder.stepConfirmation'),
    ];

    const [activeStep, setActiveStep] = useState(0);
    const [checkoutError, setCheckoutError] = useState<CheckoutTarget | null>(null);
    const [signupFailure, setSignupFailure] = useState<SignupFailure | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const {products, isPending: isProductsPending, isError: isProductsError} = useSignupProducts();
    const {
        formState: {errors},
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        trigger
    } = useForm<SignupData & SignupConfirmation>({
        defaultValues: {
            plan: 'yearly',
            owners: [{name: ''}],
            address: {country: '', state: '', zipCode: '', city: '', street: ''},
            inTbcProgram: false,
        },
    });

    const {mutate: signup, isPending} = useMutation({
        mutationKey: ['signup'],
        mutationFn: async (signupData: SignupData) => {
            const signupRequest = {
                email: signupData.emailAddress,
                herdCode: signupData.herdCode,
                password: signupData.password,
                username: signupData.username,
                farm: {
                    address: signupData.address,
                    emailAddress: signupData.emailAddress,
                    farmName: signupData.farmName,
                    farmNumber: signupData.farmNumber,
                    inTbcProgram: signupData.inTbcProgram,
                    owners: signupData.owners.map(p => p.name).filter(Boolean),
                    phone: signupData.phone,
                    vatNumber: signupData.vatNumber,
                    website: signupData.website,
                },
            };
            return await AuthService.signUp(signupRequest);
        },
        onMutate: () => setSignupFailure(null),
        onError: (error: unknown) => {
            if (getHttpStatus(error) === 409) {
                setSignupFailure('conflict');
                setActiveStep(0);
            } else {
                setSignupFailure('generic');
            }
        },
        onSuccess: async (data: string, variables: SignupData) => {
            const selectedProduct = products.find(product => product.productType === variables.plan);
            if (!selectedProduct) {
                // the account already exists, only the plan could not be determined
                setSignupFailure('noProduct');
                return;
            }
            await startCheckout({breederId: data, productId: selectedProduct.productId});
        },
    });

    const startCheckout = async (target: CheckoutTarget) => {
        try {
            const initiatedCheckout = await PaymentService.initiateCheckout(target.breederId, target.productId);
            window.location.replace(initiatedCheckout.checkoutUrl); //replace because we don't want to allow browser "back"
        } catch (e) {
            // the account already exists, so keep the target to retry without signing up again
            setCheckoutError(target);
        }
    };

    const retryCheckout = async () => {
        if (!checkoutError) return;
        setIsRetrying(true);
        await startCheckout(checkoutError);
        setIsRetrying(false);
    };

    const selectedPlan = watch('plan');
    const selectedProduct = products.find(product => product.productType === selectedPlan);

    const handleNext = async () => {
        let fieldsToValidate: any[] = [];
        if (activeStep === 0) fieldsToValidate = ['emailAddress', 'username', 'password', 'passwordConfirmation'];
        if (activeStep === 1) fieldsToValidate = ['farmName', 'owners'];
        if (activeStep === 2) fieldsToValidate = ['address.country', 'address.state', 'address.zipCode', 'address.city', 'address.street'];
        if (activeStep === 3) fieldsToValidate = ['plan'];

        const isValid = await trigger(fieldsToValidate) && (activeStep !== 3 || !!selectedProduct);
        if (isValid) {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return <AccountStep watch={watch} register={register} errors={errors}/>;
            case 1:
                return (
                    <FarmStep
                        register={register}
                        errors={errors}
                        control={control}
                    />
                );
            case 2:
                return <AddressStep register={register} errors={errors}/>;
            case 3:
                return <PlanStep watch={watch} setValue={setValue} products={products}
                                 isPending={isProductsPending} isError={isProductsError}/>;
            case 4:
                return <ConfirmationStep watch={watch} register={register} errors={errors}
                                         selectedProduct={selectedProduct}/>;
            default:
                return "Unknown step";
        }
    };

    return (
        <Box sx={{maxWidth: 600, mx: 'auto', py: 5, px: 2}}>
            <Stack alignItems="center" spacing={1} sx={{mb: 4}}>
                <Typography variant="h6" fontWeight="bold">{t('signupBreeder.title')}</Typography>
            </Stack>

            <Stepper activeStep={activeStep} sx={{mb: 4}} alternativeLabel>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel sx={{'& .MuiStepLabel-label': {fontSize: '0.75rem'}}}>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Paper sx={{p: {xs: 3, sm: 4}, mb: 3}}>
                {renderStepContent(activeStep)}
            </Paper>

            {signupFailure && (
                <Alert severity={signupFailure === 'noProduct' ? 'warning' : 'error'} sx={{mb: 3}} action={
                    signupFailure === 'generic' ? undefined : (
                        <Stack direction="row" spacing={1}>
                            {signupFailure === 'conflict' && (
                                <Button color="inherit" size="small" onClick={() => setActiveStep(0)}>
                                    {t('signupBreeder.errors.toStepOne')}
                                </Button>
                            )}
                            <Button color="inherit" size="small" onClick={() => AuthService.doLogin()}>
                                {t('signupBreeder.errors.toLogin')}
                            </Button>
                        </Stack>
                    )
                }>
                    {t(`signupBreeder.errors.${signupFailure}`)}
                </Alert>
            )}

            {checkoutError && (
                <Alert severity="error" sx={{mb: 3}} action={
                    <Stack direction="row" spacing={1}>
                        <Button color="inherit" size="small" disabled={isRetrying} onClick={retryCheckout}>
                            {t('signupBreeder.checkoutError.retry')}
                        </Button>
                        <Button color="inherit" size="small" onClick={() => AuthService.doLogin()}>
                            {t('signupBreeder.checkoutError.toLogin')}
                        </Button>
                    </Stack>
                }>
                    {t('signupBreeder.checkoutError.message')}
                </Alert>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button
                    variant="outlined"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{borderRadius: 2}}
                >
                    {t('signupBreeder.back')}
                </Button>
                <Typography variant="caption" color="textSecondary">
                    {activeStep + 1} / {steps.length}
                </Typography>
                {activeStep === steps.length - 1 ? (
                    <Button
                        variant="contained"
                        onClick={handleSubmit(form => signup(form))}
                        disabled={isPending || !!checkoutError || signupFailure === 'noProduct'}
                        sx={{borderRadius: 2}}
                    >
                        {isPending ? t('signupBreeder.submitting') : t('signupBreeder.toPayment')}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{borderRadius: 2}}
                    >
                        {t('signupBreeder.next')}
                    </Button>
                )}
            </Stack>

            <SupportNote/>
        </Box>
    );
};

export default SignupBreeder;
